import React, { useState } from 'react';
import { ActivitySuggestion as Suggestion } from '../../hooks/useActivityTracker';
import { ACTIVITY_CATEGORIES, ActivityCategory } from '../../types/user';
import { DiaryEntry } from '../../types/diary';
import { useGamification } from '../../hooks/useGamification';

interface Props {
  suggestion: Suggestion;
  onConfirm: (entry: Omit<DiaryEntry, 'id'>) => void;
  onDismiss: () => void;
}

function fmt(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function ActivitySuggestionCard({ suggestion, onConfirm, onDismiss }: Props) {
  const [cat, setCat] = useState<ActivityCategory>(suggestion.suggestedCategory);
  const [note, setNote] = useState(suggestion.suggestedText);
  const { updateStreak } = useGamification();

  const confirm = () => {
    onConfirm({
      category: cat,
      activity: note,
      startTime: Date.now() - suggestion.timeAway,
      duration: suggestion.timeAway,
      placeName: suggestion.placeName,
      source: 'suggested',
    });
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    updateStreak(dateStr);
    onDismiss();
  };

  return (
    <div className="mx-4 mb-4 card glass p-4 shadow-float tab-panel-enter">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-stabeon-text font-semibold text-sm">You were away for {fmt(suggestion.timeAway)}</p>
          <p className="text-stabeon-muted text-xs mt-0.5">
            {suggestion.placeName ? `Near ${suggestion.placeName}` : 'What were you up to?'}
          </p>
        </div>
        <button onClick={onDismiss} className="text-stabeon-muted hover:text-stabeon-text text-lg leading-none p-1 transition-colors">×</button>
      </div>

      {/* Quick category row */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        {ACTIVITY_CATEGORIES.slice(0, 6).map(c => (
          <button
            key={c.key}
            onClick={() => { setCat(c.key); setNote(c.label); }}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
              cat === c.key ? 'pill-active' : 'pill-inactive'
            }`}
          >
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      <input
        value={note}
        onChange={e => setNote(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && confirm()}
        placeholder="Add a note..."
        className="input-field w-full text-sm mb-3"
      />

      <button onClick={confirm} className="btn-primary w-full text-sm py-2.5">
        Save to diary
      </button>
    </div>
  );
}
